/**
 * CSSE prediction maths — a straight port of FreePredictorViewModel.kt so the
 * website and the app give identical numbers. Pure functions, no Angular.
 */

// 2025 CSSE standardisation values
const ENGLISH_MEAN = 34.98670057;
const ENGLISH_SD = 7.614091721;
const MATHS_MEAN = 27.30021514;
const MATHS_SD = 13.07618947;
const ENGLISH_AGE_FACTOR = 0.016617024;
const MATHS_AGE_FACTOR = 0.00894304;

export const SCALE_MIN = 240;
export const SCALE_MAX = 400;

export interface PredictorInput {
  reading: number;   // 0–40
  writing: number;   // 0–20
  maths: number;     // 0–60
  day: number;
  month: number;     // 1–12
  year: number;
}

export interface SchoolPrediction {
  school: string;
  insideCutoff: number | null;
  outsideCutoff: number | null;
  studentScore: number;
  /** Score minus the cutoff used for the bar (inside, else outside). */
  diff: number | null;
  status: string;
  tone: 'good' | 'near' | 'far' | 'none';
}

export interface PredictorResult {
  englishRaw: number;
  englishStandard: number;
  mathsStandard: number;
  finalScore: number;
  daysYounger: number;
  level: string;
  bandNote: string;
  ageNote: string;
  coachLine: string;
  schools: SchoolPrediction[];
}

const CSSE_SCHOOLS: [string, number | null, number][] = [
  ['Colchester County High School for Girls', 322, 333],
  ['Colchester Royal Grammar School', null, 343],
  ['King Edward VI Grammar School', 355, 372],
  ['Southend High School for Boys', 305, 342],
  ['Southend High School for Girls', 303, 321],
  ['Westcliff High School for Boys', 309, 351],
  ['Westcliff High School for Girls', 306, 345],
];

function isValid(i: PredictorInput): boolean {
  return i.reading >= 0 && i.reading <= 40 &&
    i.writing >= 0 && i.writing <= 20 &&
    i.maths >= 0 && i.maths <= 60 &&
    i.day >= 1 && i.day <= 31 &&
    i.month >= 1 && i.month <= 12 &&
    i.year >= 2005 && i.year <= 2030;
}

/**
 * Same as the app: days from DOB to 1 September of the birth year.
 * (Flagged in the app code as worth confirming against the CSSE table.)
 */
function daysYounger(day: number, month: number, year: number): number {
  const dob = Date.UTC(year, month - 1, day);
  const ref = Date.UTC(year, 8, 1);
  return Math.round((ref - dob) / 86_400_000);
}

export function levelFor(score: number): string {
  if (score >= 355) return 'Excellent';
  if (score >= 330) return 'Strong';
  if (score >= 315) return 'Secure';
  if (score >= 280) return 'Developing';
  return 'Emerging';
}

function bandNote(score: number): string {
  if (score >= 355) return 'Top band';
  if (score >= 330) return 'Upper band';
  if (score >= 315) return 'Middle band';
  if (score >= 280) return 'Lower band';
  return 'Early band';
}

function ageNote(days: number): string {
  if (days <= 0) return 'No age allowance applied at this date of birth.';
  const points = Math.round(days * (ENGLISH_AGE_FACTOR + MATHS_AGE_FACTOR) * 1.5);
  return `${days} days of age allowance — worth about ${points} ${points === 1 ? 'point' : 'points'} on the standard score.`;
}

function statusFor(diff: number | null): string {
  if (diff === null) return 'No data';
  if (diff >= 15) return 'Comfortable';
  if (diff >= 5) return 'On track';
  if (diff >= -5) return 'Borderline';
  if (diff >= -15) return 'Within reach';
  return 'Stretch target';
}

function toneFor(diff: number | null): SchoolPrediction['tone'] {
  if (diff === null) return 'none';
  if (diff >= 5) return 'good';
  if (diff >= -15) return 'near';
  return 'far';
}

function schoolsFor(finalScore: number): SchoolPrediction[] {
  const rounded = Math.round(finalScore);
  return CSSE_SCHOOLS.map(([school, inside, outside]) => {
    const cutoff = inside ?? outside;
    const diff = cutoff === null ? null : rounded - cutoff;
    return {
      school, insideCutoff: inside, outsideCutoff: outside,
      studentScore: finalScore, diff,
      status: statusFor(diff), tone: toneFor(diff),
    };
  }).sort((a, b) => {
    const ca = a.insideCutoff ?? a.outsideCutoff ?? Number.MAX_SAFE_INTEGER;
    const cb = b.insideCutoff ?? b.outsideCutoff ?? Number.MAX_SAFE_INTEGER;
    return (b.studentScore - cb) - (a.studentScore - ca);
  });
}

function coachLine(schools: SchoolPrediction[]): string {
  const withCutoff = schools
    .filter(s => (s.insideCutoff ?? s.outsideCutoff) !== null)
    .map(s => ({ s, gap: Math.round(s.studentScore - (s.insideCutoff ?? s.outsideCutoff)!) }));
  if (!withCutoff.length) return '';

  const cleared = withCutoff.filter(x => x.gap >= 0).length;
  const nearest = withCutoff.reduce((a, b) => (Math.abs(b.gap) < Math.abs(a.gap) ? b : a));
  const shortName = nearest.s.school.replace(' School', '');

  if (cleared > 0) {
    const tail = nearest.gap >= 0 ? `${nearest.gap} points of headroom.` : `${Math.abs(nearest.gap)} points away.`;
    return `Clear of ${cleared} of ${withCutoff.length} cutoffs. ${shortName} is the one to watch — ${tail}`;
  }
  const weeks = Math.max(1, Math.round(Math.abs(nearest.gap) / 4.5));
  return `No cutoff cleared yet. Closest is ${shortName}, ${Math.abs(nearest.gap)} points away — roughly ${weeks} ${weeks === 1 ? 'week' : 'weeks'} of focused practice.`;
}

/** Returns null when any input is out of range. */
export function predict(i: PredictorInput): PredictorResult | null {
  if (!isValid(i)) return null;

  const englishRaw = i.reading + i.writing;
  const days = daysYounger(i.day, i.month, i.year);

  const englishStandard = 100 + ((englishRaw - ENGLISH_MEAN) / ENGLISH_SD) * 15 + days * ENGLISH_AGE_FACTOR;
  const mathsStandard = 100 + ((i.maths - MATHS_MEAN) / MATHS_SD) * 15 + days * MATHS_AGE_FACTOR;
  const finalScore = 1.5 * (englishStandard + mathsStandard);
  const schools = schoolsFor(finalScore);

  return {
    englishRaw, englishStandard, mathsStandard, finalScore,
    daysYounger: days,
    level: levelFor(finalScore),
    bandNote: bandNote(finalScore),
    ageNote: ageNote(days),
    coachLine: coachLine(schools),
    schools,
  };
}

/** 0–100 position on the 240–400 scale, for bar widths. */
export function scalePercent(score: number): number {
  return Math.min(100, Math.max(0, ((score - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100));
}
